import React, { useState, useRef, useCallback } from 'react';
import { Box, Button, Typography, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Slider } from '@mui/material';
import { CloudUpload, Delete, Crop as CropIcon } from '@mui/icons-material';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../../utils/cropImage';

const LogoUpload = ({ 
  currentLogo, 
  onUpload, 
  onDelete, 
  loading = false, 
  disabled = false,
  size = 80,
  showPreview = true 
}) => {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Cropper states
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (file) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('Logo file size must be less than 2MB');
      return;
    }

    // Read the file as a data URL so we can crop it
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      setImageSrc(reader.result);
      setIsCropModalOpen(true);
    });
    reader.readAsDataURL(file);
    
    // Clear input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleDeleteClick = () => {
    if (window.confirm('Are you sure you want to delete the logo?')) {
      onDelete();
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSaveCrop = async () => {
    try {
      const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      // Convert Blob to File object to be compatible with existing upload handler
      const file = new File([croppedImageBlob], 'logo-cropped.png', { type: 'image/png' });
      
      setIsCropModalOpen(false);
      
      if (onUpload && typeof onUpload === 'function') {
        onUpload(file);
      }
    } catch (e) {
      console.error(e);
      alert('Failed to crop image');
    }
  };

  return (
    <Box>
      {showPreview && (
        <Box 
          sx={{ 
            width: size, 
            height: size, 
            border: '1px solid #ddd', 
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            mb: 2,
            backgroundColor: '#f9f9f9'
          }}
        >
          {currentLogo ? (
            <img 
              src={currentLogo.startsWith('http') ? currentLogo : `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${currentLogo.startsWith('/') ? '' : '/'}${currentLogo}`} 
              alt="Logo Preview" 
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'contain' 
              }} 
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              No Logo
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <CloudUpload />}
          onClick={handleUploadClick}
          disabled={disabled || loading}
          sx={{ minWidth: '120px' }}
        >
          {loading ? 'Uploading...' : 'Upload Logo'}
        </Button>
        
        {currentLogo && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={handleDeleteClick}
            disabled={disabled || loading}
          >
            Delete
          </Button>
        )}
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      <Box
        sx={{
          mt: 2,
          p: 2,
          border: `2px dashed ${dragActive ? '#1976d2' : '#ddd'}`,
          borderRadius: 1,
          backgroundColor: dragActive ? '#f0f8ff' : '#f9f9f9',
          textAlign: 'center',
          transition: 'all 0.2s ease',
          cursor: 'pointer'
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleUploadClick}
      >
        <Typography variant="body2" color="textSecondary">
          Drag and drop your logo here, or click to browse (Max 2MB)
        </Typography>
      </Box>

      {/* Cropper Modal */}
      <Dialog 
        open={isCropModalOpen} 
        onClose={() => setIsCropModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Crop Your Logo</DialogTitle>
        <DialogContent sx={{ p: 0, height: 400, position: 'relative' }}>
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1} // 1:1 square for logos
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ width: '50%', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">Zoom</Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              aria-labelledby="Zoom"
              onChange={(e, zoom) => setZoom(zoom)}
            />
          </Box>
          <Box>
            <Button onClick={() => setIsCropModalOpen(false)} color="inherit">
              Cancel
            </Button>
            <Button 
              onClick={handleSaveCrop} 
              color="primary" 
              variant="contained" 
              startIcon={<CropIcon />}
              sx={{ ml: 1 }}
            >
              Crop & Upload
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LogoUpload;
